import { Component, HostListener, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from "@angular/router";
import { EVENT_DETAILS } from "src/app/app-routing.module";
import { environment } from "@environment";
import { DOCUMENT } from "@angular/common";
import { AbstractComponent } from "@components";
import {
  EventFilter,
  EventItem,
  EventItemType,
  FindEventsRequest,
  LengthRange,
  ListSortOrder,
  Lookups,
  Message,
  Paging,
  Sport,
  Waypoint
} from "@model";
import { LoaderService, PublicEventsService } from "@services";
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import {getDateFromNgbDate, normalizedDate} from "@utility";

@Component({
  selector: 'app-event-calendar',
  templateUrl: './event-calendar.component.html',
  styleUrls: ['./event-calendar.component.scss']
})
export class EventCalendarComponent extends AbstractComponent implements OnInit, OnDestroy, Paging<EventItem> {
  static DEFAULT_PAGE_SIZE: number = 51;

  @Input() countries: Lookups[] = [];
  @Input() sports: Sport[] = [];
  @Input() sportsForSearch: Sport[] = [];
  @Input() countriesForSearch: Lookups[] = [];
  @Input() listOfEvents: EventItem[] = [];
  @Input({ required: true }) languages: Lookups[] = [];
  @Input() favoriteItems: EventItem[] = [];
  @Input() unreadMessages?: Message[];

  calendarType: EventItemType = EventItemType.UPCOMING;
  EventItemType = EventItemType;
  name: string = "";
  dateTo?: NgbDate;
  dateFrom?: NgbDate;
  lengthRanges: LengthRange[] = EventFilter.LENGTH_RANGES;
  selectedRange?: LengthRange;
  page: number = 1;
  hasMoreEvents: boolean = false;
  images: string[] = [1, 2, 3].map(n => `${environment.assetsImagesPath}/assets/img/carousel/carousel${n}.png`);
  environment: any = environment;
  showFabButton: boolean = false;
  sportId?: number;
  countryId?: number;
  initialDate: NgbDate;
  queryParamsFirstChange: boolean = true;
  isLoading: boolean = false;
  eventMapVisible: boolean = false;
  lat: number = environment.headquartersLat;
  lng: number = environment.headquartersLng;
  waypointList: Waypoint[] = [];

  constructor(private router: Router, private publicEventsService: PublicEventsService, private route: ActivatedRoute,
              @Inject(DOCUMENT) private document: Document, private loaderService: LoaderService) {
    super();
    const now = new Date();
    this.initialDate = new NgbDate(now.getFullYear(), now.getMonth() + 1, now.getDate());
  }

  @HostListener('window:scroll')
  toggleFabButton() {
    this.showFabButton = window.pageYOffset !== 0;
  }

  ngOnInit(): void {
    this.subscribeToRouteDataChange();
    this.handleExploreResult(this.listOfEvents);
  }

  findWayPointList(): void {
    this.waypointList = this.listOfEvents
      .filter(event => event.latitude && event.longitude)
      .map(event => ({
        latitude: event.latitude,
        longitude: event.longitude,
        city: event.city,
        country: event.country,
        pointNameEn: event.nameEng,
        pointNameHr: event.name,
        pointNameIt: event.nameIta,
        pointNameSlo: event.nameSlo,
        eventItem: event
      }));
  }

  private subscribeToRouteDataChange() {
    this.subscriptions.push(this.route.url.subscribe(urlSegments => {
      const currentUrl = urlSegments.map(segment => segment.path).join('/');
      this.calendarType = currentUrl.includes("finishedEvents") ? EventItemType.FINISHED : EventItemType.UPCOMING;
    }));

    this.subscriptions.push(this.route.queryParams.pipe(debounceTime(environment.fullTextSearchDelay), distinctUntilChanged())
      .subscribe(queryParams => {
        this.name = queryParams['name'] || "";
        this.dateFrom = queryParams['dateFrom'] ? this.getNgbDateFromString(queryParams['dateFrom']) : undefined;
        this.dateTo = queryParams['dateTo'] ? this.getNgbDateFromString(queryParams['dateTo']) : undefined;
        if(!this.dateTo && EventItemType.FINISHED === this.calendarType) this.dateTo = this.initialDate;
        if(!this.dateFrom && EventItemType.UPCOMING === this.calendarType) this.dateFrom = this.initialDate;
        this.sportId = this.getSportValue(queryParams);
        this.countryId = this.getCountryValue(queryParams);
        const from = this.getLengthValue(queryParams, true);
        const to = this.getLengthValue(queryParams, false);
        this.selectedRange = this.lengthRanges.filter(range => range.from === from && range.to === to)?.at(0)
        this.eventMapVisible = this.getEventMapVisible(queryParams);
        if (!this.queryParamsFirstChange && this.isDateValid(queryParams['dateFrom']) && this.isDateValid(queryParams['dateTo']) && this.isLengthValid(queryParams['lengthTo'])&& this.isLengthValid(queryParams['lengthFrom']) && this.isSportValid(queryParams['sport'])) {
          this.onExplore(false);
        }
        this.queryParamsFirstChange = false;
      }));
  }

  private changeQueryParams(): void {
    const queryParams: { [key: string]: any } = {};
    if (this.dateFrom) queryParams['dateFrom'] = normalizedDate(getDateFromNgbDate(this.dateFrom));
    if (this.dateTo) queryParams['dateTo'] = normalizedDate(getDateFromNgbDate(this.dateTo));
    if (this.selectedRange?.from) {
        queryParams['lengthFrom'] = this.selectedRange.from;
    }
    if (this.selectedRange?.to) {
        queryParams['lengthTo'] = this.selectedRange.to;
    }

    if (this.sportId) queryParams['sport'] = this.sportId;
    if (this.countryId) queryParams['country'] = this.countryId;
    if (this.name) queryParams['name'] = this.name;
    queryParams['eventMapVisible'] = this.eventMapVisible;
    this.router.navigate([], { relativeTo: this.route, queryParams, replaceUrl: true });
  }

  createFilter(): FindEventsRequest {
    return FindEventsRequest.createEventPageFilter(
      this.page,
      {
        name: this.name || undefined,
        lengthFrom: this.selectedRange?.from,
        lengthTo: this.selectedRange?.to,
        sport: this.sportId,
        country: this.countryId,
        ...this.getEventFilterDates()
      },
      this.calendarType === EventItemType.FINISHED ? ListSortOrder.ASC : ListSortOrder.DESC
    );
  }

  getEventFilterDates(): {
    dateTo: Date | undefined,
    dateFrom: Date | undefined,
  } {
    if (EventItemType.FINISHED === this.calendarType) {
      return {
        dateFrom: this.dateFrom ? getDateFromNgbDate(this.dateFrom) : undefined,
        dateTo: getDateFromNgbDate(this.dateTo ?? this.initialDate),
      }
    } else {
      return {
        dateTo: this.dateTo ? getDateFromNgbDate(this.dateTo) : undefined,
        dateFrom: getDateFromNgbDate(this.dateFrom ?? this.initialDate),
      }
    }
  }


  handleExploreResult(listOfEvents: EventItem[], loadMore?: boolean): void {
    if (listOfEvents && this.calendarType !== EventItemType.UPCOMING) {
      this.hasMoreEvents = listOfEvents.length > EventCalendarComponent.DEFAULT_PAGE_SIZE;
      listOfEvents = listOfEvents.slice(0, EventCalendarComponent.DEFAULT_PAGE_SIZE);
      if (loadMore && this.listOfEvents) {
        listOfEvents = this.listOfEvents.concat(listOfEvents);
      }
    }
    this.setListOfItems(listOfEvents);
  }

  setListOfItems(listOfEvents: EventItem[]): void {
    this.listOfEvents = listOfEvents.length > 0 ? listOfEvents : [];
    if (this.calendarType === EventItemType.UPCOMING) {
      this.findWayPointList();
    }
    this.page++;
  }

  onExplore(loadMore?: boolean) {
    if (!loadMore) this.page = 1;
    this.isLoading = !loadMore;
    this.loaderService.startLoading();
    const serviceMethod = this.calendarType === EventItemType.UPCOMING ? 'findEventCalendarList' : 'filterFinishedEventsList';
    this.publicEventsService[serviceMethod](this.createFilter()).subscribe(listOfEvents => {
      this.loaderService.dismissLoading();
      this.isLoading = false;
      this.handleExploreResult(listOfEvents, loadMore);
    }, (_) => {
      this.loaderService.dismissLoading();
      this.isLoading = false;
    });
  }

  loadMore(): void {
    this.onExplore(true);
  }

  isDateValid(dateValue?: string) {
    if (dateValue) {
      //Normalize the date string to yyyy-mm-dd format before parsing it to ensure compatibility across all devices and browsers
      const [year, month, day] = dateValue.split('-');
      const normalizedMonth = month.length === 1 ? `0${month}` : month;
      const normalizedDay = day.length === 1 ? `0${day}` : day;
      dateValue =  `${year}-${normalizedMonth}-${normalizedDay}`;
      return !isNaN(new Date(dateValue).getTime());
    }

    else {
      return true;
    }
  }

  isSportValid(sportValue?: string) {
    return !sportValue || this.sportsForSearch.some(sport => sport.id === +sportValue);
  }

  isCountryValid(countryValue?: string) {
    return !countryValue || this.countriesForSearch.some(country => country.id === +countryValue);
  }

  isLengthValid(lengthValue?: string | null): boolean {
    if (lengthValue === undefined || lengthValue === null || lengthValue === '') return true;

    const validValues = new Set<number>();
    EventFilter.LENGTH_RANGES.forEach(range => {
      if (range.from !== undefined) validValues.add(range.from);
      if (range.to !== undefined) validValues.add(range.to);
    });

    const parsedValue = Number(lengthValue);
    return !isNaN(parsedValue) && validValues.has(parsedValue);
  }

  getLengthValue(queryParams: any, isFrom: boolean): number | undefined {
    const rawValue = isFrom ? queryParams.lengthFrom : queryParams.lengthTo;
    if (rawValue === undefined || rawValue === null || rawValue === '') return undefined;
    return this.isLengthValid(rawValue) ? Number(rawValue) : undefined;
  }

  getSportValue(queryParams: any) {
    return this.isSportValid(queryParams.sport) ? +queryParams.sport : undefined;
  }

  getCountryValue(queryParams: any) {
    return this.isCountryValid(queryParams.country) ? +queryParams.country : undefined;
  }

  getEventMapVisible(queryParams: any) {
    return queryParams.eventMapVisible === "true";
  }

  getNgbDateFromString(dateString?: string) {
    if (dateString) {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return new NgbDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
      }
    }
    return undefined;
  }

  dateChanged(value: { from?: NgbDate, to?: NgbDate }) {
    this.dateFrom = value.from;
    this.dateTo = value.to;
    this.changeQueryParams();
  }


  onLengthRangeSelect(range?: LengthRange) {
    this.selectedRange = this.selectedRange?.id === range?.id ? undefined : range;
    this.changeQueryParams()
  }

  onSportSelect(sportId?: number) {
    this.sportId = this.sportId === sportId ? undefined : sportId;
    this.changeQueryParams();
  }

  onCountrySelect(country?: number) {
    this.countryId = this.countryId === country ? undefined : country;
    this.changeQueryParams();
  }

  handleInput(value: string) {
    this.name = value.trim();
    this.changeQueryParams();
  }

  goToEventDetailsPage(item: EventItem, isFinishedEvent: boolean ): void {
    this.router.navigate([EVENT_DETAILS], { relativeTo: this.route, queryParams: { eventId: item.id, isFinishedEvent: isFinishedEvent } });
  }

  trackByFn(index: number, item: EventItem) {
    return item.id;
  }

  scrollToTop(): void {
    this.document.body.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' });
  }

  toggleEventMapVisible(): void {
    this.eventMapVisible = !this.eventMapVisible;
    this.changeQueryParams();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
  }
}
